import React from 'react';
import { Box, ClickableCard, Divider, Text } from '@wix/design-system';
import style from './app-promo-box.module.scss';
import { AppPromotionImage } from './app-promo-box-elements/app-promotion-image';
import { SurfaceAwareClickableCard } from '../surface-aware-clickable-card';
import { AppInfoBadge } from '../app-info-badge';
import type { PricingBadgeProps } from '../pricing-badge';
import { appPromoBoxDataHooks } from './data-hooks';
import { useSiteContext } from '../../contexts/site-context';
import { shouldShowPremiumRequired } from '../app-info-badge/app-info-badge.util';
import type { BaseApp } from '../../types';
import { isAppWithBenefits } from '@wix/app-market-services';

export interface AppPromoBoxProps {
  dataHook: string;
  appData: BaseApp;
  onClick?(): void;
  href?: string;
  languageCode?: string;
}

export function AppPromoBox({
  dataHook,
  appData,
  onClick,
  href,
  languageCode = 'en',
}: AppPromoBoxProps) {
  const { isPremiumSite } = useSiteContext();
  const {
    icon,
    name,
    shortDescription,
    installation,
    promotionalImage,
    pricing,
    appBadges = [],
  } = appData;

  const appInfoBadge = (
    <Box>
      <AppInfoBadge
        isAppInstalled={!!installation?.isInstalled}
        isAppBuiltIn={!!installation?.isBuiltIn}
        isAppSetupIncomplete={!!installation?.isSetupIncomplete}
        isAppWithBenefits={isAppWithBenefits(appBadges)}
        pricing={pricing as PricingBadgeProps}
        options={{
          showPremiumRequired: shouldShowPremiumRequired(
            !!installation?.requirements?.premiumSite,
            isPremiumSite,
          ),
        }}
      />
    </Box>
  );

  return (
    <SurfaceAwareClickableCard
      containerDataHook={appPromoBoxDataHooks.container}
      containerClassName={style.promoBoxContainer}
      className={appBadges.length > 0 ? style.promoWithBadge : undefined}
      hasPadding={false}
      href={href}
      data-hook={dataHook}
      aria-label={name ?? undefined}
      onClick={(e) => {
        onClick?.();
        e.preventDefault();
      }}
    >
      <Box direction="vertical">
        <AppPromotionImage
          iconUrl={icon ?? ''}
          appName={name ?? ''}
          promotionalImage={promotionalImage ?? ''}
          isInstalled={installation?.isInstalled ?? false}
          appBadges={appBadges}
          languageCode={languageCode}
        />
        <Divider />
      </Box>
      <Box
        direction="vertical"
        verticalAlign="space-between"
        dataHook="app-details-box"
        padding="18px"
      >
        <Box direction="vertical" paddingTop="6px" height="84px">
          <Text weight="bold" dataHook="app-name">
            {name}
          </Text>
          <Box maxWidth="270px" marginTop="6px">
            <Text size="small" weight="thin">
              {shortDescription}
            </Text>
          </Box>
        </Box>
        <Box marginTop="6px">
          {installation?.isSetupIncomplete ? (
            <ClickableCard.Action>{appInfoBadge}</ClickableCard.Action>
          ) : (
            appInfoBadge
          )}
        </Box>
      </Box>
    </SurfaceAwareClickableCard>
  );
}
